// Translation catalog for the admin portal (take-order-i18n spec, Requirement 4.1).
//
// Mirrors admin-mobile-app/src/utils/i18nCatalog.js. Keys are grouped by screen
// prefix (`takeOrder.*`) so translating an additional screen ADDS a group rather
// than editing this one (Requirement 12.5).
//
// ---------------------------------------------------------------------------
// TWO RULES THAT THE TEST SUITE ENFORCES — read before editing
// ---------------------------------------------------------------------------
//
// 1. EVERY `en` VALUE IS COPIED CHARACTER-FOR-CHARACTER FROM THE PRE-FEATURE
//    SOURCE (Requirement 5.9). That includes the `…` ellipsis (U+2026, not
//    three periods), the `—` em dash (U+2014), and the literal `&` in the web
//    subtitle (which was `&amp;` in JSX and renders as `&`). An English session
//    must diff to NOTHING against the old hardcoded page. Do not "tidy" these.
//
// 2. EVERY `{token}` IN AN `en` VALUE MUST APPEAR EXACTLY ONCE IN EVERY OTHER
//    LANGUAGE'S VALUE FOR THE SAME KEY (Requirement 5.7, design Property 3).
//    Word ORDER may change freely — that is the whole point of interpolation —
//    but a token may not be dropped, duplicated, or renamed.
//
// ---------------------------------------------------------------------------
// STATUS OF THE `ta` VALUES: REVIEWED AND SIGNED OFF
// ---------------------------------------------------------------------------
//
// Reviewed and approved by the repo owner, who reads and writes Tamil
// (spec task 8, review packet at .kiro/specs/take-order-i18n/TAMIL_REVIEW.md).
// Machine translation remains out of scope by Requirement 9.9, so any future
// change here needs the same sign-off.
//
// Two deliberate choices, approved as part of that review, so they are not
// mistaken for oversights later:
//   - `To-go` -> `பார்சல்` ("parcel"), the everyday Indian-English loanword
//     restaurant staff actually use, rather than a literal rendering of "to go".
//   - `GST` stays in LATIN script, because that is how the tax is printed on
//     Indian invoices and how staff recognise it. As a result
//     `takeOrder.gstExclusive` is byte-identical in both languages BY DESIGN --
//     that is not a broken lookup, and a test asserts it.
//
// Surface-specific wording ("Click the menu" on web vs "Tap the menu" on
// mobile) uses DISTINCT keys with `.web` / `.mobile` suffixes rather than
// divergent values for one key, so the cross-surface key-set check stays
// mechanical (Requirement 12.4).

export const TRANSLATIONS = {
  en: {
    // --- Screen chrome ---
    'takeOrder.title': 'Take Order',
    'takeOrder.tabLabel': 'Take Order',
    'takeOrder.subtitle.web': 'Build a ticket & send to the POS queue',
    'takeOrder.subtitle.mobile': 'Build a ticket and send to the POS queue',
    'takeOrder.logout': 'Logout',
    'takeOrder.languageSwitcher.label': 'Language',

    // --- Loading / access / error banners ---
    'takeOrder.loadingMenu': 'Loading menu…',
    // Names the 'Take Order' permission, not 'POS Queue'. The gate moved when
    // Take Order became its own controllable tab (order-additions R2.12/R11.2);
    // these strings would otherwise send a user to request the wrong grant.
    'takeOrder.viewOnly.web':
      'You have view-only access to Take Order. Ask an admin for Edit access to send orders.',
    'takeOrder.viewOnly.mobile':
      'View-only access. Ask an admin for Take Order Edit to send orders.',
    'takeOrder.error.loadData': 'Failed to load Take Order data.',
    // --- Order additions + order-line-removal follow-up ---
    'takeOrder.add.openTickets': 'Add to an open order',
    'takeOrder.add.openTicketHint': 'Add or remove items on this order',
    // Open-order pickers (mobile today; kept here so the two catalogs stay
    // key-for-key identical).
    'takeOrder.add.openDineIn': 'Open dine-in orders',
    'takeOrder.add.openToGo': 'Open to-go orders',
    'takeOrder.add.openPickerCount': '{count} open',
    'takeOrder.add.openPickerNone': 'None open',
    'takeOrder.add.openPickerNoMatch': 'No open orders match your search.',
    'takeOrder.add.openPickerChoose': 'Select an order to update',
    // Filter inside the open-order picker. Rendered only when the list has more than
    // one row, so the common small-list case gains no chrome. '#' covers both the
    // store ticket number and the order number, which are different things to staff.
    'takeOrder.add.searchOpen': 'Search table, name, party or #',
    // Marks the order already being updated, so switching away is a deliberate act
    // rather than something a waiter discovers after the fact.
    'takeOrder.add.openPickerCurrent': 'Updating now',
    // Shown before unsent items are thrown away by switching orders. The body names
    // the count because "some items" is not enough to decide with.
    'takeOrder.add.switchTitle': 'Discard unsent items?',
    'takeOrder.add.switchBody': 'You have changes on this order that have not been sent to the kitchen. Switching orders will discard them.',
    'takeOrder.add.switchConfirm': 'Discard and switch',
    // Leaving Add_Mode for a brand-new order discards the same unsent work as
    // switching orders does, so it gets the same prompt with the outcome renamed.
    'takeOrder.add.exitBody': 'You have changes on this order that have not been sent to the kitchen. Starting a new order will discard them.',
    'takeOrder.add.exitConfirm': 'Discard and start new',
    // Last four digits only — the full number is never sent to the client.
    'takeOrder.add.phoneLast4': '••{last4}',
    'takeOrder.add.tableChip': 'Table {table}',
    'takeOrder.add.itemCount': '{count} items',
    'takeOrder.add.billStaleShort': 'bill out of date',
    'takeOrder.add.bannerTitle': 'Updating order for {target}',
    'takeOrder.add.bannerMeta': 'Order {orderNumber} · {count} items · {total}',
    // Ticket-numbering sibling of bannerMeta. Chosen at the call site when the order
    // carries an in-store ticket/token, so the banner names the order by the SAME
    // token the POS queue row shows instead of the internal order number. {orderNumber}
    // holds the resolved ticket value here -- the token name is reused so one params
    // object feeds either key.
    'takeOrder.add.bannerMetaTicket': 'Ticket {orderNumber} · {count} items · {total}',
    // Names the outcome (a new order) rather than the jargon ("ticket"). The
    // Tamil already read "start a new order", so only the English changed.
    'takeOrder.add.exit': 'Create New Order',
    'takeOrder.add.alreadyOnOrder': 'Already on this order',
    // Header for the pending batch in Add_Mode. NOT 'Current order': the order's
    // existing lines are shown in their own panel directly above this section.
    'takeOrder.add.itemsToAdd': 'Items to add',
    'takeOrder.add.noItemsToAddYet': 'No items added yet. Tap the menu to add, or edit the lines above.',
    'takeOrder.add.alreadyOnOrderSummary': '{count} items · {total}',
    'takeOrder.add.pendingChange': '(pending change)',
    'takeOrder.add.adding': 'Adding',
    'takeOrder.add.emptyBatch': 'Click the menu to add more items',
    'takeOrder.add.newOrderTotal': 'New order total',
    'takeOrder.add.wasTotal': 'Was {total}',
    'takeOrder.add.wasTotalLabel': 'Order total before',
    // Renamed from "Add to order": the button now submits BOTH new items and any
    // quantity change/removal staged on an existing line, so "Add" alone would be a
    // lie the moment a server used it purely to remove something.
    'takeOrder.add.update': 'Update Order',
    'takeOrder.add.clearBatch': 'Clear pending changes',
    'takeOrder.add.confirmTitle': 'Update this order?',
    'takeOrder.add.confirm': 'Update Order',
    'takeOrder.add.cancel': 'Cancel',
    'takeOrder.add.removingLine': 'Remove',
    'takeOrder.add.changingLineQty': 'Change to {quantity} ×',
    'takeOrder.add.billWillBeStale':
      'A bill was already printed for this order. It will be out of date after this.',
    'takeOrder.add.billStaleLong': 'The printed bill is now out of date — print it again.',
    'takeOrder.add.printHint': 'Print the updated slip for the kitchen from POS Queue.',
    'takeOrder.add.sent': 'Order {orderNumber} updated.',
    // Ticket-numbering sibling of add.sent (see bannerMetaTicket).
    'takeOrder.add.sentTicket': 'Ticket {orderNumber} updated.',
    'takeOrder.add.resultTotal': 'Order total is now {total}.',
    'takeOrder.add.error.failed': 'Failed to add the items',
    'takeOrder.add.error.adjustFailed': 'Failed to change the quantity',
    'takeOrder.add.error.noLongerOpen':
      'That order was settled or cancelled, so it can no longer be changed.',
    // Shown when a POS Queue "+ Add items" link targets a DIFFERENT order than the one
    // currently open in Add_Mode, and there are staged edits or queued items that
    // switching would discard.
    'takeOrder.add.error.finishCurrentFirst':
      'Finish or clear the order you are editing before switching to another one.',
    // Cancel-order action (order-line-removal follow-up). Gated on 'POS Queue',
    // the same tab the equivalent action on the POS Queue screen requires.
    'takeOrder.add.cancelOrder': 'Cancel Order',
    'takeOrder.add.cancelling': 'Cancelling…',
    'takeOrder.add.cancelOrderConfirm': 'Cancel order {orderNumber}? This cannot be undone.',
    // Ticket-numbering siblings of the cancel confirm/result (see bannerMetaTicket).
    'takeOrder.add.cancelOrderConfirmTicket': 'Cancel ticket {orderNumber}? This cannot be undone.',
    'takeOrder.add.cancelled': 'Order {orderNumber} cancelled.',
    'takeOrder.add.cancelledTicket': 'Ticket {orderNumber} cancelled.',
    'takeOrder.add.error.cancelFailed': 'Failed to cancel the order',
    'takeOrder.error.loadMenu': 'Failed to load Take Order menu',
    'takeOrder.error.sendFailed': 'Failed to send order',
    'takeOrder.error.sendFailedTicketKept.mobile': 'Failed to send order. Ticket preserved.',
    'takeOrder.error.network.web': 'Network error — order not sent. Ticket preserved.',
    'takeOrder.error.notAuthenticated.mobile': 'Not authenticated.',

    // --- Menu pane ---
    'takeOrder.search': 'Search menu…',
    'takeOrder.allCategories': 'All',
    // Caption on the pinned category dropdown that replaced the horizontal chip
    // strip, and the title of the sheet it opens.
    'takeOrder.categoryFilterLabel': 'Category',
    'takeOrder.itemFallback': 'Item',
    'takeOrder.inCart': '{count} in cart',
    'takeOrder.noMatch': 'No items match.',

    // --- Order type + guest/customer fields ---
    'takeOrder.orderType.dineIn': 'Dine-in',
    'takeOrder.orderType.toGo': 'To-go',
    'takeOrder.tablePlaceholder.web': 'Table (e.g. 12, A4, Patio 3)',
    'takeOrder.tableLabel.mobile': 'Table #',
    'takeOrder.tablePlaceholder.mobile': 'e.g. 12 or A4',
    'takeOrder.guests': 'Guests',
    'takeOrder.guestsPlaceholder.mobile': 'Count',
    'takeOrder.toGoSectionLabel.mobile': 'Phone / To-go customer',
    'takeOrder.customerName.web': 'Customer name (phone order)',
    'takeOrder.customerName.mobile': 'Customer name',
    'takeOrder.customerPhone': 'Phone number',

    // Mandatory-field labels. Shared by web and mobile (no .web/.mobile split) because the
    // requirement is the same on both and two strings would eventually say two things.
    // Each order type has exactly one required field: a ticket needs SOMETHING to call it
    // by, or it cannot be served -- a to-go bag with no name has nobody to hand it to.
    'takeOrder.tableLabel': 'Table number',
    'takeOrder.customerNameLabel': 'Customer name',
    // Caption for the phone field in the compact pinned strip, where the
    // placeholder is not visible once a number has been typed.
    'takeOrder.customerPhoneLabel': 'Phone',
    'takeOrder.customerNamePrompt': 'Enter a customer name to send a to-go order.',

    // --- Table party subdivision (table-party-subdivision R9.1) ---
    //
    // The party IDENTIFIER itself (`A`, `B`, `C`, `D`) is NOT a translation key and
    // never will be. It stays Latin in every language (R9.2), for the same reason
    // `GST` did: it is read aloud off a printed chit by a server, a cook and a
    // cashier, alongside Arabic-numeral table numbers. One script is safer than two
    // for a string whose only job is to be unambiguous out loud. Only the chrome
    // around it is translated.
    'takeOrder.party.label': 'Party',
    'takeOrder.party.none': 'No party',
    'takeOrder.party.select': 'Party {label}',
    'takeOrder.party.nameLabel': 'Party name (optional)',
    'takeOrder.party.namePlaceholder': 'e.g. Kumar, window side',
    'takeOrder.party.ticketHeader': 'Table {table} · Party {label}',
    'takeOrder.party.openTicketsOne': 'This table already has 1 open ticket.',
    'takeOrder.party.openTicketsMany': 'This table already has {count} open tickets.',
    'takeOrder.party.openTicketEntry': 'Party {label} · {total}',
    'takeOrder.party.openTicketNoLabel': 'No party · {total}',
    'takeOrder.party.collision': 'Party {label} is already open at this table.',
    'takeOrder.party.nextParty': 'Next party at this table',
    'takeOrder.party.hint':
      'Use this when two separate groups share one table, so each gets its own bill.',

    // Jump-to-end controls. Labels only -- the buttons themselves show an arrow.
    // --- Inventory (pos-inventory-tracking R7.3) ---
    // Staff-only strings: the remaining count reaches the Take Order projection and no
    // storefront response (R7.4). `{count}` is a plain integer in both languages.
    'takeOrder.stock.remaining': '{count} left',
    'takeOrder.stock.soldOut': 'Sold out',

    'takeOrder.scrollToTop': 'Jump to top',
    'takeOrder.scrollToBottom': 'Jump to bottom',

    // --- Ticket pane ---
    // "New Order" rather than "Current order": the pane always holds a ticket that
    // has NOT been sent yet. In Add_Mode the heading uses takeOrder.add.itemsToAdd
    // instead, so this string only ever labels a brand-new ticket.
    'takeOrder.currentOrder': 'New Order',
    'takeOrder.emptyTicket.web': 'No items yet. Click the menu to add.',
    'takeOrder.emptyTicket.mobile': 'No items yet.\nTap the menu to add.',
    'takeOrder.remove': 'Remove',
    'takeOrder.itemNotes': 'Item notes (e.g. no onions)',
    'takeOrder.orderNotes': 'Order notes (optional)',

    // --- Customizations (take-order-modifiers) ---
    //
    // Only the CHROME is translated. Group and option names render exactly as configured,
    // because `ModifierGroups` carries no i18n field yet (spec Future Extensions), and the
    // validation messages built by utils/modifierSelection.js embed those names, so they
    // stay English too. An English option name inside a Tamil sheet is honest; a machine
    // translation would end up on a kitchen chit.
    'takeOrder.customize.title': 'Customize',
    'takeOrder.customize.subtitle': 'Choose options, then add to the ticket.',
    'takeOrder.customize.missingRequired': 'Some choices are still needed:',
    'takeOrder.customize.optionsFallback': 'Options',
    'takeOrder.customize.tapToRemove': 'Tap to remove',
    'takeOrder.customize.chooseAtLeast': 'Choose at least {count}',
    'takeOrder.customize.optional': 'Optional',
    'takeOrder.customize.noOptions': 'No options available — ask a manager to check the menu.',
    'takeOrder.customize.tapAgainToClear': 'Tap the selected option again to clear it.',
    'takeOrder.customize.lineTotal': 'Item total',
    'takeOrder.customize.cancel': 'Cancel',
    'takeOrder.customize.addToTicket': 'Add to ticket',

    // --- Totals ---
    'takeOrder.subtotal': 'Subtotal',
    'takeOrder.total': 'Total',
    'takeOrder.estimateNote.web': 'Estimate — the register confirms the final amount.',
    'takeOrder.gstExclusive': 'GST ({rate}%)',
    'takeOrder.gstInclusive': 'GST ({rate}%, incl.)',
    'takeOrder.taxEstimate': 'Tax ({rate}%)',

    // --- Actions + confirmations ---
    'takeOrder.sending': 'Sending…',
    'takeOrder.send': 'Send to kitchen',
    'takeOrder.tablePrompt': 'Enter a table to send a dine-in order.',
    'takeOrder.clearTicket.web': 'Clear ticket',
    'takeOrder.sent': 'Sent to kitchen',
    'takeOrder.sentWithNumber': 'Sent to kitchen — order {orderNumber}',
    // Same confirmation, for a tenant using in-store ticket/token numbers: the waiter
    // needs the token they will call out, not the internal order number.
    //
    // The placeholder keeps the sibling's `{orderNumber}` name on purpose even though the
    // value is a ticket number, so the one call site can feed either key from a single
    // params object without inventing a second token.
    'takeOrder.sentWithTicket': 'Sent to kitchen — ticket {orderNumber}',
    'takeOrder.sentQueue.mobile': 'Order sent to POS queue.',
    // Dismiss button on the send-to-kitchen confirmation modal (mobile today; kept in
    // this catalog too so the two stay key-for-key identical).
    'takeOrder.sentModal.done': 'Done',

    // --- Review order modal ---
    // The primary Take Order button now opens this modal instead of submitting
    // straight away, so a server can read the whole ticket back before it
    // reaches the kitchen. `takeOrder.send` above is still the label on the
    // modal's own submit button, which is why it was not repointed here.
    'takeOrder.review': 'Review order',
    'takeOrder.review.title': 'Review order',
    'takeOrder.review.orderDetails': 'Order details',
    'takeOrder.review.items': 'Items',
    'takeOrder.review.orderTypeLabel': 'Order type',
    'takeOrder.review.notesLabel': 'Order notes',
    'takeOrder.review.backToMenu': 'Back to menu',
  },

  ta: {
    // --- Screen chrome ---
    'takeOrder.title': 'ஆர்டர் எடு',
    'takeOrder.tabLabel': 'ஆர்டர் எடு',
    'takeOrder.subtitle.web': 'டிக்கெட்டை உருவாக்கி POS வரிசைக்கு அனுப்பவும்',
    'takeOrder.subtitle.mobile': 'டிக்கெட்டை உருவாக்கி POS வரிசைக்கு அனுப்பவும்',
    'takeOrder.logout': 'வெளியேறு',
    'takeOrder.languageSwitcher.label': 'மொழி',

    // --- Loading / access / error banners ---
    'takeOrder.loadingMenu': 'மெனு ஏற்றப்படுகிறது…',
    // AWAITING SIGN-OFF: re-authored for the Take Order permission rename
    // (order-additions R11.2). 'Take Order' is kept in Latin script for the same
    // reason 'GST' is -- it names a control the admin sees in English in the
    // permission matrix. Confirm before release.
    'takeOrder.viewOnly.web':
      'உங்களுக்கு Take Order-இல் பார்க்கும் அனுமதி மட்டுமே உள்ளது. ஆர்டர் அனுப்ப நிர்வாகியிடம் திருத்தும் அனுமதி கேட்கவும்.',
    'takeOrder.viewOnly.mobile':
      'பார்க்கும் அனுமதி மட்டுமே. ஆர்டர் அனுப்ப நிர்வாகியிடம் Take Order திருத்தும் அனுமதி கேட்கவும்.',
    'takeOrder.error.loadData': 'ஆர்டர் தகவலை ஏற்ற முடியவில்லை.',
    // --- Order additions + order-line-removal follow-up ---
    // AWAITING SIGN-OFF. Drafted for review, consistent with the task 8 sign-off:
    // 'POS Queue' is left in Latin because it names a screen the admin sees in English.
    'takeOrder.add.openTickets': 'திறந்த ஆர்டரில் சேர்க்க',
    'takeOrder.add.openTicketHint': 'இந்த ஆர்டரில் சேர்க்க அல்லது நீக்க',
    // --- Open-order pickers --- DRAFTS, AWAITING OWNER SIGN-OFF ---
    // Not machine translated.
    'takeOrder.add.openDineIn': 'திறந்த டைன்-இன் ஆர்டர்கள்',
    'takeOrder.add.openToGo': 'திறந்த டு-கோ ஆர்டர்கள்',
    'takeOrder.add.openPickerCount': '{count} திறந்துள்ளது',
    'takeOrder.add.openPickerNone': 'எதுவும் இல்லை',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.add.openPickerNoMatch': 'உங்கள் தேடலுக்கு பொருந்தும் திறந்த ஆர்டர்கள் ஏதுமில்லை.',
    'takeOrder.add.openPickerChoose': 'புதுப்பிக்க ஒரு ஆர்டரைத் தேர்ந்தெடுக்கவும்',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    // Wording follows the existing search placeholder 'takeOrder.search'
    // ('மெனுவில் தேடு…') and reuses 'குழு' for party, as in
    // 'takeOrder.party.label'. '#' is kept as the symbol rather than spelled out.
    'takeOrder.add.searchOpen': 'மேசை, பெயர், குழு அல்லது எண் தேடு…',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.openPickerCurrent': 'இப்போது புதுப்பிக்கிறது',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.switchTitle': 'அனுப்பப்படாத பொருட்களை நிராகரிக்கவா?',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.switchBody': 'இந்த ஆர்டரில் சமையலறைக்கு அனுப்பப்படாத மாற்றங்கள் உள்ளன. ஆர்டரை மாற்றினால் அவை நிராகரிக்கப்படும்.',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.switchConfirm': 'நிராகரித்து மாற்று',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.exitBody': 'இந்த ஆர்டரில் சமையலறைக்கு அனுப்பப்படாத மாற்றங்கள் உள்ளன. புதிய ஆர்டரைத் தொடங்கினால் அவை நிராகரிக்கப்படும்.',
    // DRAFT — AWAITING OWNER SIGN-OFF
    'takeOrder.add.exitConfirm': 'நிராகரித்து புதிதாகத் தொடங்கு',
    // Digits only, no translatable text.
    'takeOrder.add.phoneLast4': '••{last4}',
    'takeOrder.add.tableChip': 'மேசை {table}',
    'takeOrder.add.itemCount': '{count} பொருட்கள்',
    'takeOrder.add.billStaleShort': 'பில் பழையது',
    'takeOrder.add.bannerTitle': '{target}-க்கான ஆர்டரைப் புதுப்பிக்கிறீர்கள்',
    'takeOrder.add.bannerMeta': 'ஆர்டர் {orderNumber} · {count} பொருட்கள் · {total}',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated. Uses 'டோக்கன்' (the
    // counter-token word Tamil Nadu restaurants use), matching sentWithTicket.
    'takeOrder.add.bannerMetaTicket': 'டோக்கன் {orderNumber} · {count} பொருட்கள் · {total}',
    'takeOrder.add.exit': 'புதிய ஆர்டர் தொடங்க',
    'takeOrder.add.alreadyOnOrder': 'இந்த ஆர்டரில் ஏற்கனவே உள்ளது',
    'takeOrder.add.itemsToAdd': 'சேர்க்க வேண்டிய பொருட்கள்',
    'takeOrder.add.noItemsToAddYet': 'இன்னும் பொருட்கள் சேர்க்கப்படவில்லை. மெனுவைத் தட்டி சேர்க்கவும், அல்லது மேலே உள்ள வரிகளைத் திருத்தவும்.',
    'takeOrder.add.alreadyOnOrderSummary': '{count} பொருட்கள் · {total}',
    'takeOrder.add.pendingChange': '(மாற்றம் நிலுவையில்)',
    'takeOrder.add.adding': 'சேர்ப்பது',
    'takeOrder.add.emptyBatch': 'மேலும் சேர்க்க மெனுவைத் தட்டவும்',
    'takeOrder.add.newOrderTotal': 'புதிய ஆர்டர் மொத்தம்',
    'takeOrder.add.wasTotal': 'முன்பு {total}',
    'takeOrder.add.wasTotalLabel': 'முன்பு இருந்த மொத்தம்',
    'takeOrder.add.update': 'ஆர்டரைப் புதுப்பிக்க',
    'takeOrder.add.clearBatch': 'நிலுவையிலுள்ள மாற்றங்களை அழிக்க',
    'takeOrder.add.confirmTitle': 'இந்த ஆர்டரைப் புதுப்பிக்கவா?',
    'takeOrder.add.confirm': 'ஆர்டரைப் புதுப்பிக்க',
    'takeOrder.add.cancel': 'ரத்து',
    'takeOrder.add.removingLine': 'நீக்கு',
    'takeOrder.add.changingLineQty': '{quantity} ஆக மாற்று ×',
    'takeOrder.add.billWillBeStale':
      'இந்த ஆர்டருக்கு ஏற்கனவே பில் அச்சிடப்பட்டது. இதற்குப் பிறகு அது பழையதாகிவிடும்.',
    'takeOrder.add.billStaleLong': 'அச்சிட்ட பில் இப்போது பழையது — மீண்டும் அச்சிடவும்.',
    'takeOrder.add.printHint': 'சமையலறைக்கான புதுப்பிக்கப்பட்ட சீட்டை POS Queue-இல் அச்சிடவும்.',
    'takeOrder.add.sent': 'ஆர்டர் {orderNumber} புதுப்பிக்கப்பட்டது.',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.add.sentTicket': 'டோக்கன் {orderNumber} புதுப்பிக்கப்பட்டது.',
    'takeOrder.add.resultTotal': 'ஆர்டர் மொத்தம் இப்போது {total}.',
    'takeOrder.add.error.failed': 'பொருட்களைச் சேர்க்க முடியவில்லை',
    'takeOrder.add.error.adjustFailed': 'அளவை மாற்ற முடியவில்லை',
    'takeOrder.add.error.noLongerOpen':
      'அந்த ஆர்டர் பணம் செலுத்தப்பட்டது அல்லது ரத்து செய்யப்பட்டது, எனவே இப்போது மாற்ற முடியாது.',
    'takeOrder.add.error.finishCurrentFirst':
      'மற்றொரு ஆர்டருக்கு மாறுவதற்கு முன், நீங்கள் திருத்தும் ஆர்டரை முடிக்கவும் அல்லது அழிக்கவும்.',
    'takeOrder.add.cancelOrder': 'ஆர்டரை ரத்து செய்',
    'takeOrder.add.cancelling': 'ரத்து செய்யப்படுகிறது…',
    'takeOrder.add.cancelOrderConfirm': 'ஆர்டர் {orderNumber}-ஐ ரத்து செய்யவா? இதை மீட்க முடியாது.',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.add.cancelOrderConfirmTicket': 'டோக்கன் {orderNumber}-ஐ ரத்து செய்யவா? இதை மீட்க முடியாது.',
    'takeOrder.add.cancelled': 'ஆர்டர் {orderNumber} ரத்து செய்யப்பட்டது.',
    'takeOrder.add.cancelledTicket': 'டோக்கன் {orderNumber} ரத்து செய்யப்பட்டது.',
    'takeOrder.add.error.cancelFailed': 'ஆர்டரை ரத்து செய்ய முடியவில்லை',
    'takeOrder.error.loadMenu': 'ஆர்டர் மெனுவை ஏற்ற முடியவில்லை',
    'takeOrder.error.sendFailed': 'ஆர்டர் அனுப்ப முடியவில்லை',
    'takeOrder.error.sendFailedTicketKept.mobile':
      'ஆர்டர் அனுப்ப முடியவில்லை. டிக்கெட் பாதுகாக்கப்பட்டுள்ளது.',
    'takeOrder.error.network.web':
      'நெட்வொர்க் பிழை — ஆர்டர் அனுப்பப்படவில்லை. டிக்கெட் பாதுகாக்கப்பட்டுள்ளது.',
    'takeOrder.error.notAuthenticated.mobile': 'அனுமதி இல்லை.',

    // --- Menu pane ---
    'takeOrder.search': 'மெனுவில் தேடு…',
    'takeOrder.allCategories': 'அனைத்தும்',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.categoryFilterLabel': 'வகை',
    'takeOrder.itemFallback': 'பொருள்',
    'takeOrder.inCart': 'கார்ட்டில் {count}',
    'takeOrder.noMatch': 'பொருந்தும் உணவுகள் இல்லை.',

    // --- Order type + guest/customer fields ---
    'takeOrder.orderType.dineIn': 'உள்ளே சாப்பிட',
    'takeOrder.orderType.toGo': 'பார்சல்',
    'takeOrder.tablePlaceholder.web': 'மேசை (எ.கா. 12, A4, Patio 3)',
    'takeOrder.tableLabel.mobile': 'மேசை எண்',
    'takeOrder.tablePlaceholder.mobile': 'எ.கா. 12 அல்லது A4',
    'takeOrder.guests': 'விருந்தினர்கள்',
    'takeOrder.guestsPlaceholder.mobile': 'எண்ணிக்கை',
    'takeOrder.toGoSectionLabel.mobile': 'தொலைபேசி / பார்சல் வாடிக்கையாளர்',
    'takeOrder.customerName.web': 'வாடிக்கையாளர் பெயர் (தொலைபேசி ஆர்டர்)',
    'takeOrder.customerName.mobile': 'வாடிக்கையாளர் பெயர்',
    'takeOrder.customerPhone': 'தொலைபேசி எண்',

    // --- Mandatory fields + jump controls --- AWAITING OWNER SIGN-OFF ---
    'takeOrder.tableLabel': 'மேசை எண்',
    'takeOrder.customerNameLabel': 'வாடிக்கையாளர் பெயர்',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.customerPhoneLabel': 'தொலைபேசி',
    'takeOrder.customerNamePrompt': 'வெளியே எடுத்துச் செல்லும் ஆர்டருக்கு வாடிக்கையாளர் பெயரை உள்ளிடவும்.',

    // --- Table party subdivision --- AWAITING OWNER SIGN-OFF ---
    // Drafts. Machine translation is out of scope by requirement, so these need the
    // owner's read before they ship, as with the earlier batches above.
    //
    // `குழு` ("group") is used for "party" rather than a transliteration, because a
    // restaurant party IS a group of diners and the English word carries an unrelated
    // celebration sense in Tamil usage.
    //
    // The `{label}` token is deliberately left to interpolate a LATIN letter into a
    // Tamil sentence (R9.2, R9.3). That is intended, not an untranslated string.
    'takeOrder.party.label': 'குழு',
    'takeOrder.party.none': 'குழு இல்லை',
    'takeOrder.party.select': 'குழு {label}',
    'takeOrder.party.nameLabel': 'குழுவின் பெயர் (விருப்பம்)',
    'takeOrder.party.namePlaceholder': 'எ.கா. குமார், ஜன்னல் பக்கம்',
    'takeOrder.party.ticketHeader': 'மேசை {table} · குழு {label}',
    'takeOrder.party.openTicketsOne': 'இந்த மேசையில் ஏற்கனவே 1 ஆர்டர் நிலுவையில் உள்ளது.',
    'takeOrder.party.openTicketsMany': 'இந்த மேசையில் ஏற்கனவே {count} ஆர்டர்கள் நிலுவையில் உள்ளன.',
    'takeOrder.party.openTicketEntry': 'குழு {label} · {total}',
    'takeOrder.party.openTicketNoLabel': 'குழு இல்லை · {total}',
    'takeOrder.party.collision': 'குழு {label} இந்த மேசையில் ஏற்கனவே நிலுவையில் உள்ளது.',
    'takeOrder.party.nextParty': 'இந்த மேசையின் அடுத்த குழு',
    'takeOrder.party.hint':
      'ஒரே மேசையில் இரு தனித்தனி குழுக்கள் அமரும்போது இதைப் பயன்படுத்தவும்; ஒவ்வொரு குழுவுக்கும் தனி பில் கிடைக்கும்.',

    // --- Inventory --- AWAITING OWNER SIGN-OFF ---
    // Drafts. Machine translation is out of scope by requirement.
    'takeOrder.stock.remaining': '{count} மீதம்',
    'takeOrder.stock.soldOut': 'விற்றுத் தீர்ந்தது',

    'takeOrder.scrollToTop': 'மேலே செல்',
    'takeOrder.scrollToBottom': 'கீழே செல்',

    // --- Ticket pane ---
    // Renamed from 'தற்போதைய ஆர்டர்' (Current order) to match the English change.
    // Owner-approved 2026-08-26; not machine translated.
    'takeOrder.currentOrder': 'புதிய ஆர்டர்',
    'takeOrder.emptyTicket.web': 'இன்னும் உணவுகள் இல்லை. சேர்க்க மெனுவைக் கிளிக் செய்யவும்.',
    'takeOrder.emptyTicket.mobile': 'இன்னும் உணவுகள் இல்லை.\nசேர்க்க மெனுவைத் தட்டவும்.',
    'takeOrder.remove': 'நீக்கு',
    'takeOrder.itemNotes': 'உணவுக் குறிப்பு (எ.கா. வெங்காயம் வேண்டாம்)',
    'takeOrder.orderNotes': 'ஆர்டர் குறிப்பு (விருப்பம்)',

    // --- Customizations (take-order-modifiers) --- AWAITING OWNER SIGN-OFF ---
    // Drafts. Machine translation is out of scope by requirement, so these need the
    // owner's read before they ship, as with the earlier take-order-i18n batch.
    'takeOrder.customize.title': 'தனிப்பயனாக்கு',
    'takeOrder.customize.subtitle': 'விருப்பங்களைத் தேர்ந்தெடுத்து, ஆர்டரில் சேர்க்கவும்.',
    'takeOrder.customize.missingRequired': 'சில தேர்வுகள் இன்னும் தேவை:',
    'takeOrder.customize.optionsFallback': 'விருப்பங்கள்',
    'takeOrder.customize.tapToRemove': 'நீக்க தட்டவும்',
    'takeOrder.customize.chooseAtLeast': 'குறைந்தது {count} தேர்ந்தெடுக்கவும்',
    'takeOrder.customize.optional': 'விருப்பம்',
    'takeOrder.customize.noOptions': 'விருப்பங்கள் இல்லை — மெனுவைச் சரிபார்க்க மேலாளரிடம் கேட்கவும்.',
    'takeOrder.customize.tapAgainToClear': 'நீக்க, தேர்ந்தெடுத்த விருப்பத்தை மீண்டும் தட்டவும்.',
    'takeOrder.customize.lineTotal': 'உணவுத் தொகை',
    'takeOrder.customize.cancel': 'ரத்து',
    'takeOrder.customize.addToTicket': 'ஆர்டரில் சேர்',

    // --- Totals ---
    'takeOrder.subtotal': 'கூட்டுத்தொகை',
    'takeOrder.total': 'மொத்தம்',
    'takeOrder.estimateNote.web': 'மதிப்பீடு — இறுதித் தொகையை பில்லிங் உறுதிப்படுத்தும்.',
    'takeOrder.gstExclusive': 'GST ({rate}%)',
    'takeOrder.gstInclusive': 'GST ({rate}%, உள்ளடங்கியது)',
    'takeOrder.taxEstimate': 'வரி ({rate}%)',

    // --- Actions + confirmations ---
    'takeOrder.sending': 'அனுப்பப்படுகிறது…',
    'takeOrder.send': 'சமையலறைக்கு அனுப்பு',
    'takeOrder.tablePrompt': 'உள்ளே சாப்பிடும் ஆர்டருக்கு மேசை எண்ணை உள்ளிடவும்.',
    'takeOrder.clearTicket.web': 'டிக்கெட்டை அழி',
    'takeOrder.sent': 'சமையலறைக்கு அனுப்பப்பட்டது',
    'takeOrder.sentWithNumber': 'சமையலறைக்கு அனுப்பப்பட்டது — ஆர்டர் {orderNumber}',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    // 'டோக்கன்' is the word Tamil Nadu restaurants actually use for a counter token,
    // rather than a literal rendering of "ticket".
    'takeOrder.sentWithTicket': 'சமையலறைக்கு அனுப்பப்பட்டது — டோக்கன் {orderNumber}',
    'takeOrder.sentQueue.mobile': 'ஆர்டர் POS வரிசைக்கு அனுப்பப்பட்டது.',
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    'takeOrder.sentModal.done': 'சரி',

    // --- Review order modal ---
    // DRAFT — AWAITING OWNER SIGN-OFF. Not machine translated.
    // `takeOrder.review` uses the infinitive button style already established by
    // `takeOrder.add.update` ('ஆர்டரைப் புதுப்பிக்க'); `.title` uses the softer
    // -வும் form the other modal headings use.
    'takeOrder.review': 'ஆர்டரைச் சரிபார்க்க',
    'takeOrder.review.title': 'ஆர்டரைச் சரிபார்க்கவும்',
    'takeOrder.review.orderDetails': 'ஆர்டர் விவரங்கள்',
    'takeOrder.review.items': 'உணவுகள்',
    'takeOrder.review.orderTypeLabel': 'ஆர்டர் வகை',
    'takeOrder.review.notesLabel': 'ஆர்டர் குறிப்பு',
    'takeOrder.review.backToMenu': 'மெனுவுக்குத் திரும்பு',
  },
};

// Every key the catalog is expected to define, derived from the default language
// so the default language is definitionally the complete set (Requirement 5.1).
export const TRANSLATION_KEYS = Object.keys(TRANSLATIONS.en);
